import { get, isEqual } from 'lodash';
import uuid from 'node-uuid';
import { CREATED, BAD_REQUEST as CONFLICTS, CONFLICT as DUPLICATES, INTERNAL_SERVER_ERROR } from 'http-status-codes';
import { FILE_ERROR, FILE_TYPE_ERROR, MAX_RECORDS_ERROR, MIN_RECORDS_ERROR, PARSER_ERROR } from 'csv-file-parser-lib';
import { formatSortCode } from '../utils/accountUtils';

export const STRUCTURE_ERROR = 'structureError';
export const FILE_TYPE = 'fileType';
export const MAX_REC = 'maxRecords';
export const PARSE_ERROR = 'parseError';
export const MIN_RECORDS = 'minRecords';
export const hasConflictData = (data) => data.some((res) => res.code === CONFLICTS);
export const hasErrorData = (data) => data.some((res) => res.code === INTERNAL_SERVER_ERROR);
export const hasDuplicateData = (data) => data.some((res) => res.code === DUPLICATES);
export const hasSuccessData = (data) => data.some((res) => res.code === CREATED);
export const getSuccessDataListCount = (data) => data.filter((res) => res.code === CREATED).length || 0;
export const getErrorData = (data) =>
	data.filter((payee) => payee.code !== CREATED).filter((payee) => payee.code !== DUPLICATES);
export const findElementInDataById = (data, element) => data.find((val) => val.id === element.id);
export const getFormattedDataFromApiResponse = (response, importedPayeesList) => {
	const apiResponses = get(response, 'payload.apiResponses', []);
	return apiResponses.map((element) => ({
		id: element.id,
		code: element.code,
		body: findElementInDataById(importedPayeesList, element),
	}));
};

export const getDataIntoObject = (data = []) => {
	const [name, display_name, sort_code, account_number, reference, error_status = null] = data;
	const rowData = {
		id: uuid.v4(),
		name,
		display_name,
		sort_code,
		account_number,
		reference,
		error_status,
	};
	return rowData;
};
export const type = (errorType) => {
	switch (errorType) {
		case FILE_ERROR:
			return STRUCTURE_ERROR;
		case FILE_TYPE_ERROR:
			return FILE_TYPE;
		case MAX_RECORDS_ERROR:
			return MAX_REC;
		case PARSER_ERROR:
			return PARSE_ERROR;
		case MIN_RECORDS_ERROR:
			return MIN_RECORDS;
		case 'userDataError':
			return true;
		default:
			return null;
	}
};
export const getContentKey = (errorType) => {
	switch (errorType) {
		case FILE_ERROR:
			return 'uploadErrorStructureError';
		case FILE_TYPE_ERROR:
			return 'uploadErrorFileType';
		case MAX_RECORDS_ERROR:
			return 'uploadErrorMaxRecords';
		case PARSER_ERROR:
			return 'uploadErrorParseError';
		case MIN_RECORDS_ERROR:
			return 'uploadErrorMinRecords';
		default:
			return null;
	}
};
export const getFormattedData = (importedPayeesList) =>
	importedPayeesList.map((payee) => ({ ...payee, sort_code: formatSortCode(payee.sort_code) }));
export const getFormattedDataBackend = (importedPayeesList) =>
	importedPayeesList.map((payee) => ({ ...payee.body, sort_code: formatSortCode(payee.body.sort_code) }));

export const handleImportPayeesPartialSuccess = (formattedData, successMessage, addNotification) => (dispatch) => {
	let contentKey = 'serverError';
	let notificationKey = '';
	let errorType = '';

	if ((hasConflictData(formattedData) || hasErrorData(formattedData)) && hasDuplicateData(formattedData)) {
		// has at least 1 duplicate and error data
		notificationKey = 'backendBenImportPartialSuccessWithError';
		errorType = 'partialSuccessWithErrorAndDuplicates';
	} else if (hasConflictData(formattedData) || hasErrorData(formattedData)) {
		// found at least one 400 here - meaning conflict data
		notificationKey = 'backendBenImportPartialSuccessWithError';
		errorType = 'partialSuccessWithError';
	} else if (hasDuplicateData(formattedData)) {
		// found at least one 409 here - meaning duplicate or apicall failure data but no conflict data
		notificationKey = 'backendBenImportPartialSuccessWithoutError';
		errorType = 'partialSuccessWithDuplicates';
	} else {
		notificationKey = successMessage;
		errorType = 'success';
		contentKey = null;
	}
	return dispatch(
		addNotification(notificationKey, {
			formattedData,
			contentKey,
			errorType,
			isServerValidation: true,
		})
	);
};

export const handleImportPayeesNoSuccess = (formattedData, addNotification) => (dispatch) => {
	// not even a single successful ben got created
	const contentKey = 'serverError';
	let notificationKey = '';
	let errorType = '';

	if ((hasConflictData(formattedData) || hasErrorData(formattedData)) && hasDuplicateData(formattedData)) {
		notificationKey = 'backendBenImportWithAllErrorsAndDuplicates';
		errorType = 'errorsAndDuplicatesOnly';
	} else if (hasConflictData(formattedData) || hasErrorData(formattedData)) {
		notificationKey = 'backendBenImportWithAllError';
		errorType = 'errorOnly';
	} else {
		notificationKey = 'backendBenImportWithAllDuplicates';
		errorType = 'duplicatesOnly';
	}
	return dispatch(
		addNotification(notificationKey, {
			formattedData,
			contentKey,
			errorType,
			isServerValidation: true,
		})
	);
};

export const getBeneficiaryRules = () => {
	const validPayeeName = /^([a-zA-Z0-9\s/.-]){1,40}$/;
	const validPayeeNickname = /^([a-zA-Z0-9\s/.,()'&_-]){1,50}$/;
	const validSortCode = /^\d{6}$/;
	const validAccountNumber = /^\d{8}$/;
	const validPayeeReference = /^([a-zA-Z0-9\s/.,()'&_-]){1,18}$/;

	const headers = ['Payee Name', 'Payee Nickname', 'Payee Sort Code', 'Payee Account Number', 'Payment Reference'];

	const isHeaderRow = (row) => row && isEqual(row.slice(0, headers.length), headers);

	const rules = [
		{ name: 'payee name', test: (value) => validPayeeName.test(value) },
		{ name: 'payee nickname', test: (value) => validPayeeNickname.test(value) },
		{ name: 'sort code', test: (value) => validSortCode.test(value) },
		{ name: 'account number', test: (value) => validAccountNumber.test(value) },
		{ name: 'payee reference', test: (value) => validPayeeReference.test(value) },
	];
	return {
		isHeaderRow,
		rules,
	};
};

export const filterForPayeesInFileUpload = ({ code }, isPaymentFlow) => {
	if (isPaymentFlow) return [CREATED, DUPLICATES].includes(code);
	return [CREATED].includes(code);
};
